// src/pages/TradeTracker/Controls.jsx
import { useEffect } from "react";

export default function Controls({
  availableOrgs,
  selectedOrg,
  setSelectedOrg,
  availableParties,
  selectedParty,
  setSelectedParty,
  selectedDate,
  setSelectedDate,
}) {
  const multiOrg = availableOrgs.length > 1;

  // Ensure a trade party is always selected when an org with parties is active
  useEffect(() => {
    if (!selectedOrg) return;
    if (!availableParties?.length) return;

    const hasSelected =
      selectedParty &&
      availableParties.some((p) => p.canon_name === selectedParty);

    if (!hasSelected) {
      // Default to first party in alpha order (availableParties is already ordered)
      setSelectedParty(availableParties[0].canon_name);
    }
  }, [selectedOrg, availableParties, selectedParty, setSelectedParty]);

  return (
    <div className="flex flex-wrap items-center gap-4 sm:gap-6 min-w-0">
      {/* Org select */}
      <div className="flex flex-col">
        <label className="text-xs font-medium text-muted-foreground mb-1">
          Select Org
        </label>
        <div className="relative">
          <select
            aria-label="Organization"
            className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                       focus:outline-none focus:ring-2 focus:ring-ring max-w-full"
            value={selectedOrg?.id || ""}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                setSelectedOrg(null);
              } else {
                const org = availableOrgs.find((c) => c.id === id) || null;
                setSelectedOrg(org);
              }
            }}
          >
            {multiOrg && <option value="">Select organization</option>}
            {availableOrgs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
          </svg>
        </div>
      </div>

      {/* Party select — only show if an org is selected */}
      {selectedOrg && (
        <div className="flex flex-col">
          <label className="text-xs font-medium text-muted-foreground mb-1">
            Select Trade Party
          </label>
          <div className="relative">
            <select
              aria-label="Trade party"
              className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-ring max-w-full"
              value={selectedParty || ""}
              onChange={(e) => setSelectedParty(e.target.value)}
              disabled={!availableParties.length}
            >
              {availableParties.map((p) => (
                <option key={p.id} value={p.canon_name}>
                  {p.canon_name}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
            </svg>
          </div>
        </div>
      )}

      {/* As-of date */}
      <div className="flex flex-col">
        <label className="text-xs font-medium text-muted-foreground mb-1">
          Select Valuation Date
        </label>
        <input
          aria-label="As of date"
          type="date"
          className="h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                     focus:outline-none focus:ring-2 focus:ring-ring px-3"
          value={selectedDate ?? ""}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </div>
    </div>
  );
}
